import { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import api from "../../lib/api"
import { toastError, toastSuccess } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { btnSecondary } from "../../components/admin/FormComponents"
import { LoadState } from "@/components/ui/LoadState"

// Fase 1 de openspec/changes/section-quiz-question-types — 10 tipos de
// texto/inputs. content/correct_answer/answer tienen forma dependiente de
// question_type, igual que en la API (ver lib/section-quiz/question-types.ts).
type QuestionType =
  | "multiple_choice" | "multiple_select" | "true_false" | "short_answer" | "essay"
  | "fill_blank" | "numerical" | "code" | "matching" | "ordering"

type ReviewQuestion = {
  index: number
  question_text: string
  question_type: QuestionType
  content: Record<string, unknown>
  correct_answer: unknown
  explanation?: string | null
  answer: unknown
  is_correct: boolean
}

type AttemptDetail = {
  id: string
  is_team_attempt: boolean
  team_name: string | null
  student_names: string[]
  student_email: string | null
  score: number
  passed: boolean
  overridden: boolean
  source: "live" | "backup"
  questions: ReviewQuestion[]
}

const QUESTION_TYPE_LABEL: Record<QuestionType, string> = {
  multiple_choice: "Opción múltiple",
  multiple_select: "Selección múltiple",
  true_false: "Verdadero/Falso",
  short_answer: "Respuesta corta",
  essay: "Ensayo",
  fill_blank: "Completar espacios",
  numerical: "Numérica",
  code: "Código",
  matching: "Emparejar",
  ordering: "Ordenar",
}

function Chip({ tone, children }: { tone: "correct" | "wrong" | "neutral"; children: React.ReactNode }) {
  const cls =
    tone === "correct"
      ? "bg-green-100 font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300"
      : tone === "wrong"
      ? "bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-300"
      : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
  return <span className={`rounded px-2 py-1 text-xs ${cls}`}>{children}</span>
}

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.map((x) => String(x)) : []
}

function asNumberArray(v: unknown): number[] {
  return Array.isArray(v) ? v.map((x) => Number(x)) : []
}

/** Renderiza contenido + respuesta del estudiante + respuesta correcta según el tipo de pregunta. */
function QuestionBody({ q }: { q: ReviewQuestion }) {
  const content = q.content ?? {}

  switch (q.question_type) {
    case "multiple_choice": {
      const options = asStringArray(content.options)
      const correctIdx = Number(q.correct_answer)
      const answerIdx = q.answer === null || q.answer === undefined ? null : Number(q.answer)
      return (
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {options.map((opt, i) => (
            <Chip key={i} tone={i === correctIdx ? "correct" : i === answerIdx ? "wrong" : "neutral"}>
              {i === correctIdx ? "✓ " : i === answerIdx ? "✗ " : ""}{opt}
            </Chip>
          ))}
        </div>
      )
    }

    case "multiple_select": {
      const options = asStringArray(content.options)
      const correctSet = new Set(asNumberArray(q.correct_answer))
      const answerSet = new Set(asNumberArray(q.answer))
      return (
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {options.map((opt, i) => (
            <Chip key={i} tone={correctSet.has(i) ? "correct" : answerSet.has(i) ? "wrong" : "neutral"}>
              {correctSet.has(i) ? "✓ " : answerSet.has(i) ? "✗ " : ""}{opt}
            </Chip>
          ))}
        </div>
      )
    }

    case "true_false": {
      const correct = Boolean(q.correct_answer)
      const answer = q.answer === null || q.answer === undefined ? null : Boolean(q.answer)
      return (
        <div className="flex gap-1.5">
          {[true, false].map((v) => (
            <Chip key={String(v)} tone={v === correct ? "correct" : v === answer ? "wrong" : "neutral"}>
              {v === correct ? "✓ " : v === answer ? "✗ " : ""}{v ? "Verdadero" : "Falso"}
            </Chip>
          ))}
        </div>
      )
    }

    case "short_answer": {
      const accepted = asStringArray(q.correct_answer)
      return (
        <div className="space-y-1 text-xs">
          <p><span className="text-gray-500">Respuesta del estudiante:</span> <span className="font-medium text-gray-800 dark:text-white">{typeof q.answer === "string" && q.answer ? q.answer : "(sin respuesta)"}</span></p>
          <p className="text-gray-500">Aceptadas: {accepted.join(" · ")}</p>
        </div>
      )
    }

    case "essay":
    case "code": {
      return (
        <div className="space-y-1 text-xs">
          <p className="whitespace-pre-wrap"><span className="text-gray-500">Respuesta del estudiante:</span> <span className="font-medium text-gray-800 dark:text-white">{typeof q.answer === "string" && q.answer ? q.answer : "(sin respuesta)"}</span></p>
          <p className="text-gray-500">Criterio de corrección: {String(q.correct_answer)}</p>
        </div>
      )
    }

    case "fill_blank": {
      const correctBlanks = asStringArray(q.correct_answer)
      const answerBlanks = asStringArray(q.answer)
      return (
        <div className="space-y-1">
          {typeof content.textWithBlanks === "string" && (
            <p className="text-xs text-gray-500">{content.textWithBlanks}</p>
          )}
          <div className="flex flex-wrap gap-1.5">
            {correctBlanks.map((correct, i) => (
              <Chip key={i} tone={answerBlanks[i]?.trim().toLowerCase() === correct.trim().toLowerCase() ? "correct" : "wrong"}>
                #{i + 1}: {answerBlanks[i] || "(vacío)"} {answerBlanks[i]?.trim().toLowerCase() !== correct.trim().toLowerCase() ? `(correcta: ${correct})` : ""}
              </Chip>
            ))}
          </div>
        </div>
      )
    }

    case "numerical": {
      const ca = (q.correct_answer ?? {}) as { value?: number; tolerance?: number }
      return (
        <p className="text-xs">
          <span className="text-gray-500">Respuesta del estudiante:</span>{" "}
          <span className="font-medium text-gray-800 dark:text-white">{q.answer === null || q.answer === undefined ? "(sin respuesta)" : String(q.answer)}</span>
          {" · "}
          <span className="text-gray-500">Correcta: {ca.value}{ca.tolerance ? ` ± ${ca.tolerance}` : ""}</span>
        </p>
      )
    }

    case "matching": {
      const left = asStringArray(content.left)
      const right = asStringArray(content.right)
      const correct = asNumberArray(q.correct_answer)
      const answer = asNumberArray(q.answer)
      return (
        <div className="space-y-1">
          {left.map((label, i) => {
            const isCorrect = answer[i] === correct[i]
            return (
              <p key={i} className="text-xs">
                <span className="text-gray-700 dark:text-gray-300">{label}</span> →{" "}
                <Chip tone={isCorrect ? "correct" : "wrong"}>
                  {answer[i] !== undefined ? right[answer[i]] ?? "?" : "(sin respuesta)"}
                </Chip>
                {!isCorrect && <span className="ml-1 text-gray-500">(correcta: {right[correct[i]]})</span>}
              </p>
            )
          })}
        </div>
      )
    }

    case "ordering": {
      const items = asStringArray(content.items)
      const correct = asNumberArray(q.correct_answer)
      const answer = asNumberArray(q.answer)
      return (
        <div className="space-y-1 text-xs">
          <p className="text-gray-500">Orden del estudiante: {answer.length ? answer.map((i) => items[i]).join(" → ") : "(sin respuesta)"}</p>
          <p className="text-gray-500">Orden correcto: {correct.map((i) => items[i]).join(" → ")}</p>
        </div>
      )
    }

    default:
      return null
  }
}

export default function SectionQuizAttemptDetailPage() {
  const { attemptId } = useParams<{ attemptId: string }>()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<AttemptDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [overriding, setOverriding] = useState(false)

  const load = useCallback(async () => {
    if (!attemptId) return
    setLoading(true)
    try {
      const res = await api.get(`/academy/section-quizzes/attempts/${attemptId}/admin`)
      setDetail(res.data.data)
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }, [attemptId])

  useEffect(() => { void load() }, [load])

  const handleOverride = useCallback(async (passed: boolean) => {
    if (!attemptId) return
    setOverriding(true)
    try {
      await api.put(`/academy/section-quizzes/attempts/${attemptId}/override`, { passed })
      toastSuccess(passed ? "Intento marcado como aprobado." : "Intento marcado como no aprobado.")
      await load()
    } catch (err) {
      toastError(err)
    } finally {
      setOverriding(false)
    }
  }, [attemptId, load])

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Contenido", { label: "Exámenes IA", to: "/instructor/section-quizzes" }, "Detalle de intento"]}
      title="Revisión de intento"
      subtitle={detail ? detail.student_names.join(", ") : "Cargando..."}
      actions={<button type="button" className={btnSecondary} onClick={() => navigate(-1)}>Volver</button>}
      tabs={[]}
      metrics={
        detail
          ? [
              { label: "Puntaje", value: String(detail.score) },
              { label: "Resultado", value: detail.passed ? "Aprobado" : "No aprobado", tone: detail.passed ? "good" : "danger" },
              { label: "Origen de preguntas", value: detail.source === "live" ? "Generado en vivo" : "Banco de respaldo" },
              { label: "Modo", value: detail.is_team_attempt ? `Equipo${detail.team_name ? ` — ${detail.team_name}` : ""}` : "Individual" },
            ]
          : []
      }
    >
      {loading && <LoadState />}

      {!loading && detail && (
        <div className="space-y-4">
          {detail.overridden && (
            <div className="rounded-lg bg-amber-100 px-3 py-2 text-xs font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
              Resultado anulado manualmente por un instructor.
            </div>
          )}

          <div className="space-y-3">
            {detail.questions.map((q, idx) => (
              <div key={q.index} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{idx + 1}. {q.question_text}</p>
                  <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    {QUESTION_TYPE_LABEL[q.question_type] ?? q.question_type}
                  </span>
                </div>
                <QuestionBody q={q} />
                {q.explanation && <p className="mt-2 text-xs italic text-gray-500">💡 {q.explanation}</p>}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 border-t border-gray-200 pt-4 dark:border-gray-800">
            <span className="mr-1 text-xs text-gray-500">Sobrescribir resultado:</span>
            <button type="button" disabled={overriding} onClick={() => handleOverride(true)}
              className="rounded-lg border border-green-300 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50 dark:border-green-700 dark:text-green-300 dark:hover:bg-green-900/20">
              Marcar aprobado
            </button>
            <button type="button" disabled={overriding} onClick={() => handleOverride(false)}
              className="rounded-lg border border-error-300 px-3 py-1.5 text-xs font-medium text-error-700 hover:bg-error-50 disabled:opacity-50 dark:border-error-700 dark:text-error-300 dark:hover:bg-error-900/20">
              Marcar no aprobado
            </button>
          </div>
        </div>
      )}
    </ModuleLayout>
  )
}
